"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import cn from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface MetaItem {
  id: string;
  meta_logo: string;
  meta_alt_image?: string;
  meta_priority: number;
  meta_status?: boolean;
}

interface AccreditationData {
  id: number;
  page_id: number;
  background_color?: string;
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  metadata?: MetaItem[];
  status?: boolean;
}

interface AccreditationProps {
  data: AccreditationData;
}

// Logos per view: 2 on mobile, 4 on desktop (PRD). The carousel (and arrows)
// appear only when the count exceeds the per-view at that breakpoint.
const ITEMS_PER_VIEW_MOBILE = 2;
const ITEMS_PER_VIEW_DESKTOP = 4;

// Built-in maroon circle arrow (Figma vuesax/arrow-circle). Used unless the CMS
// provides a custom navigation image. `pointLeft` flips the chevron.
const ArrowCircle = ({ pointLeft }: { pointLeft?: boolean }) => (
  <svg
    viewBox="0 0 40 40"
    fill="none"
    aria-hidden="true"
    className="size-full"
  >
    <circle cx="20" cy="20" r="20" fill="#9F1C34" />
    <path
      d={pointLeft ? "M22.5 14.5 17 20l5.5 5.5" : "M17.5 14.5 23 20l-5.5 5.5"}
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LogoCard = ({ item }: { item: MetaItem }) => (
  <div
    className={cn(
      "flex items-center justify-center w-full",
      "h-[100px] sm:h-[130px] md:h-[160px]",
    )}
  >
    {item?.meta_logo && (
      <Image
        src={item.meta_logo}
        alt={item.meta_alt_image || "Accreditation logo"}
        width={272}
        height={160}
        className="h-full w-full object-contain"
      />
    )}
  </div>
);

const Accreditation2 = ({ data }: { data: AccreditationProps }) => {
  const ref = useRef<HTMLElement>(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const {
    background_color = "#FFFFFF",
    title = "",
    title_text_color = "#101112",
    sub_title = "",
    sub_title_text_color = "#101112",
    metadata = [],
    left_navigation_icon,
    left_navigation_icon_alt,
    right_navigation_icon,
    right_navigation_icon_alt,
  } = data?.data || {};

  // Active logos, ordered by priority. Memoized so the transform is not re-run
  // on every render (LCP-sensitive widget).
  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: MetaItem) => item?.meta_status == true)
      .sort((a: MetaItem, b: MetaItem) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  // Carousel renders when the count exceeds the mobile per-view (2). Below that,
  // a static centered row is used. Arrows are then hidden on desktop (sm+) when
  // everything fits in the 4-per-view (CSS-only — no viewport JS, keeps LCP clean).
  const count = sortedMetadata.length;
  const showCarousel = count > ITEMS_PER_VIEW_MOBILE;
  const fitsDesktop = count <= ITEMS_PER_VIEW_DESKTOP;

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "accreditation_section");
  //   }
  // }, []);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (!data?.data?.status) return null;
  if (sortedMetadata.length === 0) return null;

  const hasHeader = Boolean(title) || Boolean(sub_title);

  return (
    <section
      ref={ref}
      style={{ backgroundColor: background_color }}
      className={cn(
        "flex w-full flex-col items-center justify-center gap-10",
        "px-6 py-5 sm:px-10 sm:py-[30px] md:px-[60px] md:py-10 lg:px-20 xl:px-40 xl:py-[60px]",
      )}
    >
      {hasHeader && (
        <div className="flex w-full flex-col items-center gap-4 text-center sm:gap-2.5 md:gap-3 lg:gap-4 xl:gap-5">
          {title && (
            <h2
              className={cn(
                // Heading — always 600 (semibold). Sizes per breakpoint:
                // mobile 28 · 1080 28 · 1230 32 · 1430 36 · 1750 42
                "font-semibold leading-[1.25] tracking-[-0.42px]",
                "text-[28px] sm:text-[28px] md:text-[32px] lg:text-[36px] xl:text-[42px] xl:tracking-[-0.63px]",
              )}
              style={{ color: title_text_color }}
            >
              {title}
            </h2>
          )}
          {sub_title && (
            <p
              className={cn(
                "leading-[1.6] xl:leading-[1.65]",
                // Sub-heading sizes: mobile 14 · 1080 16 · 1230 16 · 1430 17 · 1750 18
                "text-[14px] sm:text-[16px] md:text-[16px] lg:text-[17px] xl:text-[18px]",
                // Sub-heading weights: mobile 500 · 1080 400 · 1230 400 · 1430 500 · 1750 400
                "font-medium sm:font-normal md:font-normal lg:font-medium xl:font-normal",
              )}
              style={{ color: sub_title_text_color }}
            >
              {sub_title}
            </p>
          )}
        </div>
      )}

      {showCarousel ? (
        // More than 2 logos: carousel. Mobile (2/view) always gets arrows;
        // desktop (4/view) hides them when all logos fit (fitsDesktop).
        <div className="flex w-full items-center justify-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={scrollPrev}
            aria-label={left_navigation_icon_alt || "Previous"}
            className={cn(
              "shrink-0 cursor-pointer transition-opacity",
              "size-6 sm:size-10",
              fitsDesktop && "sm:hidden",
              !canScrollPrev && "opacity-50",
            )}
          >
            {left_navigation_icon ? (
              <Image
                src={left_navigation_icon}
                alt={left_navigation_icon_alt || "Previous"}
                width={52}
                height={52}
                className="size-full object-contain"
              />
            ) : (
              <ArrowCircle pointLeft />
            )}
          </button>

          <div className="min-w-0 flex-1 overflow-hidden" ref={emblaRef}>
            {/* Center the row on desktop when nothing scrolls there (≤4). */}
            <div className={cn("-mx-3 flex", fitsDesktop && "sm:justify-center")}>
              {sortedMetadata.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className="min-w-0 shrink-0 basis-1/2 px-3 sm:basis-1/4"
                >
                  <LogoCard item={item} />
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={scrollNext}
            aria-label={right_navigation_icon_alt || "Next"}
            className={cn(
              "shrink-0 cursor-pointer transition-opacity",
              "size-6 sm:size-10",
              fitsDesktop && "sm:hidden",
              !canScrollNext && "opacity-50",
            )}
          >
            {right_navigation_icon ? (
              <Image
                src={right_navigation_icon}
                alt={right_navigation_icon_alt || "Next"}
                width={52}
                height={52}
                className="size-full object-contain"
              />
            ) : (
              <ArrowCircle />
            )}
          </button>
        </div>
      ) : (
        // 2 or fewer logos: static, centered row (no carousel, no arrows).
        <div className="-mx-3 flex w-full flex-wrap items-center justify-center gap-y-6">
          {sortedMetadata.map((item, index) => (
            <div
              key={`${item.id}-${index}`}
              className="min-w-0 shrink-0 basis-1/2 px-3 sm:basis-1/4"
            >
              <LogoCard item={item} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default Accreditation2;
